import { useState, useEffect } from 'react';
import { api } from '@/lib/api-client';
import { Search, Download, Check, Filter } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from '@/components/ui/sonner';

const categories = ['All', 'Productivity', 'Developer', 'Data', 'Social', 'Business', 'Entertainment', 'Smart Home', 'Health'];

export default function SkillsMarketplace() {
  const [skills, setSkills] = useState([]);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [installing, setInstalling] = useState(null);
  const [installed, setInstalled] = useState([]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadForm, setUploadForm] = useState({ name: '', category: 'Productivity', description: '', repoUrl: '' });

  useEffect(() => { api.getSkills().then(setSkills); }, []);

  const install = async (id) => {
    setInstalling(id);
    await api.installSkill(id, 'inst_001');
    setInstalled(prev => [...prev, id]);
    setInstalling(null);
    toast.success('Skill installed successfully');
  };

  const handleUpload = () => {
    if (!uploadForm.name || !uploadForm.description) { toast.error('Name and description required'); return; }
    const newSkill = { id: `sk_custom_${Date.now()}`, name: uploadForm.name, category: uploadForm.category, installs: 0, description: uploadForm.description, author: 'You', verified: false };
    setSkills(prev => [newSkill, ...prev]);
    setUploadOpen(false);
    setUploadForm({ name: '', category: 'Productivity', description: '', repoUrl: '' });
    toast.success('Skill uploaded to marketplace');
  };

  const filtered = skills.filter(s => {
    if (category !== 'All' && s.category !== category) return false;
    if (search && !s.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div data-testid="skills-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white font-mono">Skills Marketplace</h1>
        <button data-testid="upload-skill-btn" onClick={() => setUploadOpen(true)} className="border border-zinc-800 text-zinc-400 font-mono text-sm rounded-sm px-5 py-2.5 hover:border-zinc-700 transition-all flex items-center gap-2">
          <Download size={14} className="rotate-180" /> Upload Skill
        </button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
          <input data-testid="skills-search" type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search skills..." className="w-full bg-[#111] border border-zinc-800 text-white rounded-sm pl-10 pr-4 py-2.5 text-sm font-mono focus:border-emerald-500/50 outline-none placeholder-zinc-600" />
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        {categories.map(c => (
          <button key={c} data-testid={`category-${c.toLowerCase()}`} onClick={() => setCategory(c)} className={`px-3 py-1.5 rounded-sm font-mono text-xs transition-all ${category === c ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30' : 'border border-zinc-800 text-zinc-500 hover:border-zinc-700'}`}>
            {c}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map(skill => (
          <div key={skill.id} data-testid={`skill-${skill.id}`} className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-5 hover:border-zinc-700 transition-all">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-sm font-mono text-white font-bold">{skill.name}</h3>
                <p className="text-xs font-mono text-zinc-600">{skill.category}</p>
              </div>
              {skill.verified && <span className="bg-emerald-500/10 text-emerald-400 text-xs font-mono px-2 py-0.5 rounded-sm border border-emerald-500/20">Verified</span>}
            </div>
            <p className="text-xs text-zinc-500 leading-relaxed mb-4">{skill.description}</p>
            <div className="flex items-center justify-between">
              <span className="text-xs font-mono text-zinc-600">{skill.installs.toLocaleString()} installs</span>
              <button data-testid={`install-${skill.id}`} onClick={() => install(skill.id)} disabled={installing === skill.id || installed.includes(skill.id)} className={`text-xs font-mono px-3 py-1.5 rounded-sm transition-all ${installed.includes(skill.id) ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20'} disabled:opacity-50`}>
                {installed.includes(skill.id) ? 'Installed' : installing === skill.id ? 'Installing...' : 'Install'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Upload Skill Dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="bg-[#0A0A0A] border-zinc-800 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white font-mono">Upload Skill</DialogTitle>
            <DialogDescription className="text-zinc-500 font-mono text-xs">Share your custom skill with the community</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="block text-xs font-mono text-zinc-500 mb-1.5">Skill Name *</label>
              <input data-testid="upload-skill-name" type="text" value={uploadForm.name} onChange={e => setUploadForm({...uploadForm, name: e.target.value})} className="w-full bg-[#111] border border-zinc-800 text-white rounded-sm px-4 py-2.5 text-sm font-mono focus:border-emerald-500/50 outline-none placeholder-zinc-600" placeholder="My Custom Skill" />
            </div>
            <div>
              <label className="block text-xs font-mono text-zinc-500 mb-1.5">Category</label>
              <select value={uploadForm.category} onChange={e => setUploadForm({...uploadForm, category: e.target.value})} className="w-full bg-[#111] border border-zinc-800 text-white rounded-sm px-4 py-2.5 text-sm font-mono focus:border-emerald-500/50 outline-none">
                {categories.filter(c => c !== 'All').map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-mono text-zinc-500 mb-1.5">Description *</label>
              <textarea data-testid="upload-skill-desc" rows={3} value={uploadForm.description} onChange={e => setUploadForm({...uploadForm, description: e.target.value})} className="w-full bg-[#111] border border-zinc-800 text-white rounded-sm px-4 py-2.5 text-sm font-mono focus:border-emerald-500/50 outline-none placeholder-zinc-600 resize-none" placeholder="What does this skill do?" />
            </div>
            <div>
              <label className="block text-xs font-mono text-zinc-500 mb-1.5">Repository URL</label>
              <input type="url" value={uploadForm.repoUrl} onChange={e => setUploadForm({...uploadForm, repoUrl: e.target.value})} className="w-full bg-[#111] border border-zinc-800 text-white rounded-sm px-4 py-2.5 text-sm font-mono focus:border-emerald-500/50 outline-none placeholder-zinc-600" placeholder="https://github.com/user/skill" />
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={() => setUploadOpen(false)} className="flex-1 border border-zinc-800 text-zinc-400 font-mono text-sm rounded-sm py-2.5 hover:border-zinc-700 transition-all">Cancel</button>
              <button data-testid="submit-upload-skill" onClick={handleUpload} className="flex-1 bg-emerald-500 text-black font-mono font-bold text-sm rounded-sm py-2.5 hover:bg-emerald-400 transition-all click-scale shadow-[0_0_15px_rgba(16,185,129,0.3)]">Upload</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
